import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import PanelSkeleton from "../PanelSkeleton";
import Pagination from "../Pagination";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { FaRegTrashAlt, FaCamera, FaWhatsapp } from "react-icons/fa";
import { ImSpinner2 } from "react-icons/im";
import { HiCreditCard } from "react-icons/hi";
import { BsSearch } from "react-icons/bs";
import { FiScissors, FiShare2 } from "react-icons/fi";
import {
  MdClose,
  MdKeyboardArrowDown,
  MdAttachMoney,
  MdArrowForward,
  MdAddPhotoAlternate,
  MdPrint,
  MdAdd,
  MdFilterList,
  MdDeleteOutline,
} from "react-icons/md";
import { mapToArabic } from "../../utils/mapToArabic";
import ProductImage from "../ProductImage";
import styles from "../../styles/orderspanel.module.css";
import logoSrc from "../../assets/logo.webp";

/* ─────────────────────────────────────────────────────────────────────────
   Print order — A4 blob-based print window
   ───────────────────────────────────────────────────────────────────────── */
const STATUS_BG = {
  new: "#e7f0fa",
  preparing: "#fdf0e3",
  printing: "#EDE9F7",
  pressing: "#E3F4F0",
  wrapping: "#FAE8EF",
  "out for delivery": "#f5ede0",
  delivered: "#e8f7ee",
  cancelled: "#fdebe7",
};
const STATUS_COLOR = {
  new: "#3b82c4",
  preparing: "#e07c2a",
  printing: "#7C5CBF",
  pressing: "#2E9E85",
  wrapping: "#C4547A",
  "out for delivery": "#8c6f3d",
  delivered: "#2f9e57",
  cancelled: "#e0533d",
};

const toBase64 = (url) =>
  fetch(url)
    .then((r) => r.blob())
    .then(
      (b) =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(b);
        }),
    );

const printOrder = async (
  o,
  total,
  discount,
  net,
  dateStr,
  showMoney = true,
  deposit = 0,
) => {
  const logoDataUrl = await toBase64(logoSrc).catch(() => null);
  const statusText = mapToArabic(o.status)?.text || o.status;
  const paymentText = mapToArabic(o.payment)?.text || o.payment;

  const itemsRows = o.items
    .map(
      (item) => `
    <tr>
      <td>${item.name}</td>
      <td>كود ${item.code ?? "-"}</td>
      <td style="direction:ltr;text-align:center">${item.width} × ${item.height} سم</td>
      <td>${mapToArabic(item.material)?.text || item.material}</td>
      <td style="text-align:center">${item.quantity}</td>
      ${showMoney ? `<td style="text-align:center">${item.price} ₪</td><td style="text-align:center;font-weight:700">${(item.price * item.quantity).toFixed(2)} ₪</td>` : ""}
    </tr>`,
    )
    .join("");

  const html = `<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
  <meta charset="UTF-8"/>
  <title>طلب رقم ${o.id}</title>
  <style>
    @page { size: A4; margin: 15mm 18mm; }
    *     { box-sizing: border-box; margin: 0; padding: 0; }
    body  { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; color: #333; font-size: 13px; direction: rtl; background: #fff; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 18px; border-bottom: 2px solid #c98a52; padding-bottom: 14px; }
    .brand img { height: 48px; width: auto; display: block; }
    .order-meta .order-num  { font-size: 18px; font-weight: 800; color: #333; text-align: left; }
    .order-meta .order-date { font-size: 12px; color: #888; margin-top: 3px; text-align: left; }
    .status-badge { display: inline-block; padding: 3px 12px; border-radius: 999px; font-size: 12px; font-weight: 700; margin-top: 6px; }
    .section-title { font-size: 11px; font-weight: 800; color: #9B773D; text-transform: uppercase; letter-spacing: .05em; border-bottom: 1px solid #efe4d6; padding-bottom: 5px; margin: 18px 0 10px; }
    .info-grid  { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 24px; background: #fbf4ec; border: 1px solid #efe4d6; border-radius: 8px; padding: 12px 15px; }
    .info-row   { display: flex; gap: 8px; align-items: baseline; font-size: 13px; }
    .info-label { color: #a08a6b; font-weight: 600; white-space: nowrap; min-width: 90px; }
    .info-value { color: #333; }
    .note-block { background: #fffbf4; border: 1px dashed #e7c9b7; border-radius: 8px; padding: 10px 14px; font-size: 13px; color: #333; line-height: 1.65; }
    table   { width: 100%; border-collapse: collapse; }
    th      { background: #c98a52; color: #fff; padding: 8px 10px; font-size: 12px; font-weight: 700; text-align: right; }
    td      { padding: 8px 10px; font-size: 12.5px; border-bottom: 1px solid #efe4d6; vertical-align: middle; }
    tr:nth-child(even) td { background: #fbf4ec; }
    .totals     { margin-top: 18px; display: flex; justify-content: flex-end; }
    .totals-box { background: #fbf4ec; border: 1px solid #efe4d6; border-radius: 8px; padding: 12px 18px; min-width: 240px; }
    .totals-row { display: flex; justify-content: space-between; gap: 24px; padding: 4px 0; font-size: 13px; }
    .totals-label { color: #a08a6b; }
    .totals-val   { font-weight: 700; color: #333; }
    .totals-divider { border: none; border-top: 1px solid #e4d3bd; margin: 6px 0; }
    .discount-val   { color: #e0533d; }
    .totals-net     { font-size: 15px; font-weight: 800; color: #9B773D; }
    .footer { margin-top: 28px; border-top: 1px solid #efe4d6; padding-top: 10px; text-align: center; font-size: 11px; color: #a08a6b; }
    @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="brand">${logoDataUrl ? `<img src="${logoDataUrl}" alt="فزاكر" />` : "فزاكر"}</div>
    <div class="order-meta">
      <div class="order-num">طلب # ${o.id}</div>
      <div class="order-date">${dateStr}</div>
      <span class="status-badge" style="background:${STATUS_BG[o.status] || "#f5f5f5"};color:${STATUS_COLOR[o.status] || "#666"}">${statusText}</span>
    </div>
  </div>
  <div class="section-title">بيانات العميل</div>
  <div class="info-grid">
    <div class="info-row"><span class="info-label">الاسم</span><span class="info-value">${o.name}</span></div>
    <div class="info-row"><span class="info-label">الهاتف</span><span class="info-value" style="direction:ltr">${o.mobile}</span></div>
    <div class="info-row"><span class="info-label">البريد</span><span class="info-value" style="direction:ltr;font-size:12px">${o.email}</span></div>
    <div class="info-row"><span class="info-label">العنوان</span><span class="info-value">${o.street}${o.district ? `، ${o.district}` : ""}</span></div>
    <div class="info-row"><span class="info-label">منطقة التوصيل</span><span class="info-value">${o.area}</span></div>
    <div class="info-row"><span class="info-label">طريقة الدفع</span><span class="info-value">${paymentText}</span></div>
    ${o.salesChannel ? `<div class="info-row"><span class="info-label">قناة البيع</span><span class="info-value">${o.salesChannel.icon ?? ""} ${o.salesChannel.name}</span></div>` : ""}
    ${showMoney && o.promocode ? `<div class="info-row"><span class="info-label">كوبون</span><span class="info-value">${o.promocode} (${o.discount}٪)</span></div>` : ""}
  </div>
  ${o.note ? `<div class="section-title">ملاحظة</div><div class="note-block">${o.note}</div>` : ""}
  <div class="section-title">المنتجات</div>
  <table>
    <thead><tr><th>المنتج</th><th>الكود</th><th>الأبعاد</th><th>المادة</th><th style="text-align:center">الكمية</th>${showMoney ? '<th style="text-align:center">السعر</th><th style="text-align:center">الإجمالى</th>' : ""}</tr></thead>
    <tbody>${itemsRows}</tbody>
  </table>
  ${
    showMoney
      ? `
  <div class="totals">
    <div class="totals-box">
      <div class="totals-row"><span class="totals-label">الإجمالى</span><span class="totals-val">${total.toFixed(2)} ₪</span></div>
      <div class="totals-row"><span class="totals-label">رسوم التوصيل</span><span class="totals-val">${o.fees || 0} ₪</span></div>
      ${o.promocode ? `<div class="totals-row"><span class="totals-label">خصم (${o.discount}٪)</span><span class="totals-val discount-val">- ${discount} ₪</span></div>` : ""}
      <hr class="totals-divider"/>
      <div class="totals-row"><span class="totals-label" style="font-weight:700;color:#333">الصافى</span><span class="totals-val totals-net">${net} ₪</span></div>
      ${deposit > 0 ? `
      <hr class="totals-divider"/>
      <div class="totals-row"><span class="totals-label">العربون المدفوع</span><span class="totals-val" style="color:#2f9e57">${Number(deposit).toFixed(2)} ₪</span></div>
      <div class="totals-row"><span class="totals-label" style="font-weight:700;color:#e0533d">المبلغ المتبقي</span><span class="totals-val" style="color:#e0533d;font-size:15px;font-weight:800">${(Number(net) - Number(deposit)).toFixed(2)} ₪</span></div>
      ` : ""}
    </div>
  </div>`
      : ""
  }
  <div class="footer">فزاكر • تم إنشاء هذا المستند تلقائياً</div>
</body>
</html>`;

  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank", "width=900,height=1100");
  if (!win) return;
  win.addEventListener("load", () => {
    win.focus();
    win.print();
    URL.revokeObjectURL(url);
  });
};

/* ─────────────────────────────────────────────────────────────────────────
   Cutting Sheet
   ───────────────────────────────────────────────────────────────────────── */
const CuttingSheet = ({ orders, statusFilter, hasMore }) => {
  const [open, setOpen] = useState(true);

  const summary = useMemo(() => {
    const relevant =
      statusFilter === "new" || statusFilter === "preparing"
        ? orders
        : orders.filter((o) => o.status === "new" || o.status === "preparing");

    const pairMap = {};
    const widthMap = {};
    const heightMap = {};

    for (const o of relevant) {
      for (const item of o.items) {
        if (!item.width && !item.height) continue;
        const qty = item.quantity || 1;
        const key = `${item.width}×${item.height}|${item.material || ""}`;
        pairMap[key] = (pairMap[key] || 0) + qty;
        if (item.width)
          widthMap[item.width] = (widthMap[item.width] || 0) + qty * 2;
        if (item.height)
          heightMap[item.height] = (heightMap[item.height] || 0) + qty * 2;
      }
    }

    const pairs = Object.entries(pairMap)
      .map(([key, qty]) => {
        const [dims, mat] = key.split("|");
        const [w, h] = dims.split("×");
        return { w, h, mat, qty };
      })
      .sort((a, b) => b.qty - a.qty);

    if (!pairs.length) return null;

    return {
      pairs,
      widths: Object.entries(widthMap).sort(
        ([a], [b]) => Number(b) - Number(a),
      ),
      heights: Object.entries(heightMap).sort(
        ([a], [b]) => Number(b) - Number(a),
      ),
      total: pairs.reduce((s, p) => s + p.qty, 0),
    };
  }, [orders, statusFilter]);

  if (!summary) return null;

  return (
    <div className={styles.cuttingSheet}>
      <button className={styles.cuttingHead} onClick={() => setOpen((o) => !o)}>
        <span className={styles.cuttingTitleWrap}>
          <FiScissors className={styles.cuttingIcon} />
          <span className={styles.cuttingTitle}>قائمة القطع</span>
          {hasMore && (
            <span className={styles.pageNoteBadge}>الصفحة الحالية فقط</span>
          )}
        </span>
        <span className={styles.cuttingMeta}>
          <span className={styles.cuttingTotalBadge}>{summary.total} قطعة</span>
          <MdKeyboardArrowDown
            className={`${styles.sheetChevron}${open ? ` ${styles.sheetChevronOpen}` : ""}`}
          />
        </span>
      </button>
      {open && (
        <div className={styles.cuttingBody}>
          <div className={styles.pairsSection}>
            <span className={styles.cutSectionLabel}>الأبعاد والمادة</span>
            <div className={styles.pairsGrid}>
              {summary.pairs.map((p, i) => (
                <div
                  key={i}
                  className={styles.pairCard}
                  style={{ animationDelay: `${i * 0.04}s` }}
                >
                  <div className={styles.pairDims}>
                    <span className={styles.pairNum}>{p.w}</span>
                    <span className={styles.pairSep}>×</span>
                    <span className={styles.pairNum}>{p.h}</span>
                    <span className={styles.pairUnit}>سم</span>
                  </div>
                  {p.mat && (
                    <span className={styles.pairMat}>
                      {mapToArabic(p.mat).text}
                    </span>
                  )}
                  <span className={styles.pairQtyBadge}>{p.qty} قطعة</span>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.dimGroup}>
            <span className={styles.dimGroupLabel}>إجمالى العرض</span>
            <div className={styles.dimChips}>
              {summary.widths.map(([w, qty]) => (
                <span key={w} className={styles.dimChip}>
                  <span className={styles.dimChipVal}>{w} سم</span>
                  <span className={styles.dimChipQty}>{qty}</span>
                </span>
              ))}
            </div>
          </div>
          <div className={styles.dimGroup}>
            <span className={styles.dimGroupLabel}>إجمالى الارتفاع</span>
            <div className={styles.dimChips}>
              {summary.heights.map(([h, qty]) => (
                <span key={h} className={styles.dimChip}>
                  <span className={styles.dimChipVal}>{h} سم</span>
                  <span className={styles.dimChipQty}>{qty}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   Status maps
   ───────────────────────────────────────────────────────────────────────── */
const STATUS_CARD = {
  new: styles.cardNew,
  preparing: styles.cardPrep,
  printing: styles.cardPrint,
  pressing: styles.cardPress,
  wrapping: styles.cardWrap,
  "out for delivery": styles.cardOut,
  delivered: styles.cardDone,
  cancelled: styles.cardCancel,
};
const STATUS_BADGE = {
  new: styles.badgeNew,
  preparing: styles.badgePrep,
  printing: styles.badgePrint,
  pressing: styles.badgePress,
  wrapping: styles.badgeWrap,
  "out for delivery": styles.badgeOut,
  delivered: styles.badgeDone,
  cancelled: styles.badgeCancel,
};

/* ─────────────────────────────────────────────────────────────────────────
   Empty manual-order item
   ───────────────────────────────────────────────────────────────────────── */
const emptyItem = () => ({
  name: "",
  code: "",
  width: "",
  height: "",
  material: "matte",
  price: "",
  quantity: 1,
  productId: null,
  dimensionId: null,
});

/* ═══════════════════════════════════════════════════════════════════════════
   OrdersPanel
   ═══════════════════════════════════════════════════════════════════════════ */
const OrdersPanel = () => {
  /* ── Search ── */
  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams, setSearchParams] = useState(new URLSearchParams());
  const [moveSearch, setMoveSearch] = useState(false);
  const [searching, setSearching] = useState(false);

  /* ── Filters ── */
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [statusFilter, setStatusFilter] = useState(null);
  const [photosFilter, setPhotosFilter] = useState(null); // null | 'yes' | 'no'
  const [sourceFilter, setSourceFilter] = useState(null); // 'website' | 'manual' | null
  const [channelFilter, setChannelFilter] = useState(null); // channelId | null
  const [materialFilter, setMaterialFilter] = useState(null); // 'matte' | 'gloss' | null
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [channels, setChannels] = useState([]);

  /* ── Order cards ── */
  const [expanded, setExpanded] = useState({});
  const [changing, setChanging] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState({});
  const [deletingPhoto, setDeletingPhoto] = useState(false);

  /* ── Manual order form ── */
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [manualForm, setManualForm] = useState({
    name: "",
    mobile: "",
    email: "",
    district: "",
    street: "",
    note: "",
    delivery_area: "",
    delivery_fees: "",
    payment: "cash on delivery",
    sales_channel_id: "",
    deposit: "",
  });
  const [manualItems, setManualItems] = useState([emptyItem()]);
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [catalogDimensions, setCatalogDimensions] = useState([]);

  /* ── Pagination ── */
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(10);

  /* ── Data ── */
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [fetchTick, setFetchTick] = useState(0);

  const token = useSelector((s) => s.auth?.token);
  const photoInputRef = useRef({});
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const panelRefs = useRef({});
  const authCfg = useMemo(
    () => ({
      withCredentials: true,
      headers: { Authorization: `Bearer ${token}` },
    }),
    [token],
  );

  const refetch = useCallback(() => setFetchTick((t) => t + 1), []);

  /* ── Scroll-aware search bar ── */
  useEffect(() => {
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      setMoveSearch(window.scrollY > lastScrollY);
      lastScrollY = window.scrollY;
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!searchTerm) setSearchParams(new URLSearchParams());
  }, [searchTerm]);

  /* ── Fetch sales channels once ── */
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/sales-channels`)
      .then((r) => setChannels(r.data?.data ?? []))
      .catch(() => {});
  }, []);

  /* ── Fetch delivery zones, products, and dimensions once for manual form ── */
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/orders/delivery`, authCfg)
      .then((r) => setDeliveryZones(r.data?.data ?? []))
      .catch(() => {});
    axios
      .get(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/dashboardFetch?page=1&limit=500`,
        authCfg,
      )
      .then((r) => setCatalogProducts(r.data?.products ?? []))
      .catch(() => {});
    axios
      .get(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/dimensions`,
        authCfg,
      )
      .then((r) => setCatalogDimensions(r.data?.data ?? []))
      .catch(() => {});
  }, [token, authCfg]);

  /* ── Main data fetch ── */
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    setFetchError(null);

    const params = new URLSearchParams();
    params.set("page", currentPage);
    params.set("limit", ordersPerPage);
    if (searchParams.toString())
      searchParams.forEach((v, k) => params.append(k, v));
    if (statusFilter) params.set("filter", statusFilter);
    if (photosFilter === "yes") params.set("hasPhotos", "true");
    if (photosFilter === "no") params.set("hasPhotos", "false");
    if (sourceFilter) params.set("source", sourceFilter);
    if (channelFilter != null) params.set("channelId", channelFilter);
    if (materialFilter) params.set("material", materialFilter);
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);

    axios
      .get(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/dashboardFetch?${params.toString()}`,
        authCfg,
      )
      .then((res) => {
        if (!cancelled) {
          setData(res.data);
          setSearching(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          if (err.response?.status !== 304) setFetchError(err);
          setSearching(false);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    token,
    currentPage,
    searchParams,
    statusFilter,
    photosFilter,
    sourceFilter,
    channelFilter,
    materialFilter,
    dateFrom,
    dateTo,
    fetchTick,
    authCfg,
    ordersPerPage,
  ]);

  /* ── Active filter count for badge ── */
  const activeFilterCount = [
    statusFilter,
    photosFilter !== null ? true : null,
    sourceFilter,
    channelFilter != null ? true : null,
    materialFilter,
    dateFrom || dateTo ? true : null,
  ].filter(Boolean).length;

  const clearAllFilters = () => {
    setStatusFilter(null);
    setPhotosFilter(null);
    setSourceFilter(null);
    setChannelFilter(null);
    setMaterialFilter(null);
    setDateFrom("");
    setDateTo("");
    setCurrentPage(1);
  };

  /* ── Search handlers ── */
  const handleClear = () => {
    setSearchTerm("");
    inputRef.current?.focus();
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setStatusFilter(null);
    setSourceFilter(null);
    setChannelFilter(null);
    setPhotosFilter(null);
    const terms = searchTerm.trim().split(/\s+/).filter(Boolean);
    if (!terms.length) return;
    const newParams = new URLSearchParams();
    terms.forEach((t) => newParams.append("search", t));
    if (searchParams.toString() !== newParams.toString()) {
      setSearching(true);
      setSearchParams(newParams);
      setCurrentPage(1);
    }
  };

  /* ── Derived data ── */
  const isLoadingInitial = loading || !data;
  const orders = data?.orders || [];
  const totalOrders = data?.totalCount ? Number(data.totalCount) : 0;
  const totalPages = Math.ceil(totalOrders / ordersPerPage);

  /* ── Card expand ── */
  const toggleExpand = (id) => {
    const panel = panelRefs.current[id];
    const upperPanel = panel.querySelector(".upperPanel");
    if (expanded[id]) {
      panel.style.height = `${panel.scrollHeight}px`;
      void panel.offsetHeight;
      panel.style.height = `${upperPanel.scrollHeight + 20}px`;
      setTimeout(() => setExpanded((prev) => ({ ...prev, [id]: false })), 500);
    } else {
      panel.style.height = `${upperPanel.scrollHeight + 20}px`;
      void panel.offsetHeight;
      setExpanded((prev) => ({ ...prev, [id]: true }));
      setTimeout(() => {
        panel.style.height = `${panel.scrollHeight}px`;
      }, 10);
    }
  };

  /* ── Status progression ── */
  const nextStatus = (current) => {
    switch (current) {
      case "new":
        return "preparing";
      case "preparing":
        return "printing";
      case "printing":
        return "pressing";
      case "pressing":
        return "wrapping";
      case "wrapping":
        return "out for delivery";
      case "out for delivery":
        return "delivered";
      default:
        return null;
    }
  };

  const handleStatus = async (id, userId, name, email, next) => {
    if (!next) return;
    confirmAlert({
      message: `تغيير حالة الطلب إلى ${mapToArabic(next).text} ؟`,
      buttons: [
        {
          label: "نعم",
          onClick: async () => {
            setChanging(true);
            try {
              await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/orders/${id}/updateStatus`,
                { userId, name, email, status: next },
                authCfg,
              );
              toast.success(
                <span className="toast">
                  تم تغيير حالة الطلب إلى {mapToArabic(next).text}
                </span>,
              );
              refetch();
            } catch {
              toast.error(
                <span className="toast">
                  لم نتمكن من تغيير حالة الطلب. حاول مرة اخرى
                </span>,
              );
            } finally {
              setChanging(false);
            }
          },
        },
        { label: "لا" },
      ],
    });
  };

  const handleCancel = (id, userId, email) => {
    confirmAlert({
      message: `إلغاء طلب رقم ${id} ؟`,
      buttons: [
        {
          label: "نعم",
          onClick: async () => {
            setCancelling(true);
            try {
              await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/orders/${id}/dashboardCancel`,
                { userId, email },
                authCfg,
              );
              toast.success(
                <span className="toast">تم إلغاء الطلب بنجاح</span>,
              );
              refetch();
            } catch {
              toast.error(
                <span className="toast">
                  لم نتمكن من إلغاء الطلب. حاول مرة اخرى
                </span>,
              );
            } finally {
              setCancelling(false);
            }
          },
        },
        { label: "لا" },
      ],
    });
  };

  /* ── Photos ── */
  const handleAddPhotos = async (orderId, files) => {
    if (!files?.length) return;
    const formData = new FormData();
    Array.from(files).forEach((f) => formData.append("photos", f));
    setUploadingPhotos((prev) => ({ ...prev, [orderId]: true }));
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/photos`,
        formData,
        authCfg,
      );
      toast.success(<span className="toast">تم رفع الصور بنجاح</span>);
      refetch();
    } catch {
      toast.error(<span className="toast">فشل رفع الصور. حاول مرة اخرى</span>);
    } finally {
      setUploadingPhotos((prev) => ({ ...prev, [orderId]: false }));
      if (photoInputRef.current[orderId])
        photoInputRef.current[orderId].value = "";
    }
  };

  const handleDeletePhoto = (orderId, photoId) => {
    confirmAlert({
      message: "حذف هذه الصورة الحية؟",
      buttons: [
        {
          label: "نعم",
          onClick: async () => {
            setDeletingPhoto(true);
            try {
              await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/photos/${photoId}`,
                authCfg,
              );
              toast.success(<span className="toast">تم حذف الصورة بنجاح</span>);
              refetch();
            } catch {
              toast.error(
                <span className="toast">فشل حذف الصورة. حاول مرة اخرى</span>,
              );
            } finally {
              setDeletingPhoto(false);
            }
          },
        },
        { label: "لا" },
      ],
    });
  };

  /* ── Manual order ── */
  const resetManualForm = () => {
    setManualForm({
      name: "",
      mobile: "",
      email: "",
      district: "",
      street: "",
      note: "",
      delivery_area: "",
      delivery_fees: "",
      payment: "cash on delivery",
      sales_channel_id: "",
      deposit: "",
    });
    setManualItems([emptyItem()]);
  };

  const handleManualZoneChange = (area) => {
    const zone = deliveryZones.find((z) => z.area === area);
    if (!zone || area === "__custom") {
      setManualForm((f) => ({ ...f, delivery_area: area }));
      return;
    }
    const hasBig = manualItems.some((item) => {
      const dim = catalogDimensions.find((d) => d.id === item.dimensionId);
      return dim?.vanType === "big";
    });
    const fees = hasBig ? zone.bigVanFees : zone.smallVanFees;
    setManualForm((f) => ({
      ...f,
      delivery_area: area,
      delivery_fees: String(fees),
    }));
  };

  const addManualItem = () => setManualItems((prev) => [...prev, emptyItem()]);
  const removeManualItem = (i) =>
    setManualItems((prev) => prev.filter((_, idx) => idx !== i));
  const updateManualItem = (i, field, val) =>
    setManualItems((prev) =>
      prev.map((item, idx) => (idx === i ? { ...item, [field]: val } : item)),
    );

  const recalcFeesForItems = (items) => {
    const zone = deliveryZones.find((z) => z.area === manualForm.delivery_area);
    if (!zone) return;
    const hasBig = items.some((item) => {
      const dim = catalogDimensions.find((d) => d.id === item.dimensionId);
      return dim?.vanType === "big";
    });
    const fees = hasBig ? zone.bigVanFees : zone.smallVanFees;
    setManualForm((f) => ({ ...f, delivery_fees: String(fees) }));
  };

  const handleItemProductChange = (i, opt) => {
    setManualItems((prev) =>
      prev.map((item, idx) => {
        if (idx !== i) return item;
        if (!opt) return { ...item, productId: null, name: "", code: "" };
        return {
          ...item,
          productId: opt.value,
          name: opt.product.name,
          code: opt.product.code || "",
        };
      }),
    );
  };

  const handleItemDimensionChange = (i, dimId) => {
    const newItems = manualItems.map((item, idx) => {
      if (idx !== i) return item;
      if (!dimId) return { ...item, dimensionId: null };
      const dim = catalogDimensions.find((d) => d.id === dimId);
      if (!dim) return { ...item, dimensionId: dimId };
      const price = item.material === "gloss" ? dim.glossPrice : dim.mattePrice;
      return {
        ...item,
        dimensionId: dim.id,
        width: String(dim.width),
        height: String(dim.height),
        price: String(price),
      };
    });
    setManualItems(newItems);
    recalcFeesForItems(newItems);
  };

  const handleItemMaterialChange = (i, material) => {
    setManualItems((prev) =>
      prev.map((item, idx) => {
        if (idx !== i) return item;
        const dim = catalogDimensions.find((d) => d.id === item.dimensionId);
        const price = dim
          ? String(material === "gloss" ? dim.glossPrice : dim.mattePrice)
          : item.price;
        return { ...item, material, price };
      }),
    );
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    for (const item of manualItems) {
      if (
        !item.name.trim() ||
        !item.width ||
        !item.height ||
        !item.price ||
        !item.quantity
      ) {
        toast.error(<span className="toast">أكمل بيانات جميع المنتجات</span>);
        return;
      }
    }
    setManualSaving(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/manualCreate`,
        {
          shipping_details: {
            name: manualForm.name,
            mobile: manualForm.mobile,
            ...(manualForm.email ? { email: manualForm.email } : {}),
            district: manualForm.district || undefined,
            street: manualForm.street,
            note: manualForm.note || undefined,
          },
          delivery_area: manualForm.delivery_area,
          delivery_fees: Number(manualForm.delivery_fees) || 0,
          payment: manualForm.payment,
          deposit: Number(manualForm.deposit) || 0,
          sales_channel_id: manualForm.sales_channel_id
            ? Number(manualForm.sales_channel_id)
            : undefined,
          items: manualItems.map((i) => ({
            name: i.name.trim(),
            ...(i.code ? { code: i.code } : {}),
            width: Number(i.width),
            height: Number(i.height),
            material: i.material,
            price: Number(i.price),
            quantity: Number(i.quantity),
          })),
        },
        authCfg,
      );
      toast.success(<span className="toast">تم إنشاء الطلب بنجاح</span>);
      setShowManualForm(false);
      resetManualForm();
      refetch();
    } catch {
      toast.error(
        <span className="toast">فشل إنشاء الطلب. حاول مرة أخرى</span>,
      );
    } finally {
      setManualSaving(false);
    }
  };

  /* ── Guards ── */
  if (!token)
    return (
      <div className={styles.panel}>
        <p className="warn">انتهت جلستك. سجّل دخولك مجدداً للمتابعة.</p>
      </div>
    );
  if (fetchError)
    return (
      <div className={styles.panel}>
        <p className="warn">حدث خطأ اثناء تحميل البيانات. حاول مرة اخرى</p>
      </div>
    );

  /* ═══════════════════════════════════════════════════════════════════════
     Render
     ═══════════════════════════════════════════════════════════════════════ */
  return (
    <div className={styles.panel}>
      {/* ── Search + Add button ── */}
      <div className={styles.topBar}>
        <form
          onSubmit={handleSearch}
          className={moveSearch ? "search move" : "search"}
          style={{ flex: 1 }}
        >
          <input
            type="text"
            placeholder="بحث فى الطلبات . . ."
            maxLength={30}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            ref={inputRef}
            required
          />
          {searchTerm && <MdClose className="clear" onClick={handleClear} />}
          <button type="submit" disabled={searching} className="buttonNoBorder">
            {searching ? (
              <ImSpinner2 className="smallspinner" />
            ) : (
              <BsSearch className="searchiconsmall" />
            )}
          </button>
        </form>
        <button
          className={styles.manualAddBtn}
          onClick={() => {
            setShowManualForm((v) => !v);
            resetManualForm();
          }}
        >
          <MdAdd /> إضافة طلب
        </button>
      </div>

      {/* ── Manual Order Form ── */}
      {showManualForm && (
        <div className={styles.manualForm}>
          <div className={styles.manualFormHead}>
            <span className={styles.manualFormTitle}>إضافة طلب يدوي</span>
            <button
              type="button"
              className={styles.manualFormClose}
              onClick={() => setShowManualForm(false)}
            >
              <MdClose />
            </button>
          </div>

          <form onSubmit={handleManualSubmit}>
            {/* Customer info */}
            <p className={styles.manualSection}>بيانات العميل</p>
            <div className={styles.manualGrid}>
              {[
                { label: "الاسم *", field: "name", required: true },
                { label: "الهاتف *", field: "mobile", required: true },
                { label: "البريد الإلكتروني", field: "email", type: "email" },
                { label: "الشارع *", field: "street", required: true },
                { label: "الحي", field: "district" },
              ].map(({ label, field, required, type }) => (
                <div key={field} className={styles.manualField}>
                  <label className={styles.manualLabel}>{label}</label>
                  <input
                    className={styles.manualInput}
                    type={type || "text"}
                    value={manualForm[field]}
                    onChange={(e) =>
                      setManualForm((f) => ({ ...f, [field]: e.target.value }))
                    }
                    required={required}
                    maxLength={100}
                  />
                </div>
              ))}
              <div className={styles.manualField}>
                <label className={styles.manualLabel}>ملاحظة</label>
                <input
                  className={styles.manualInput}
                  value={manualForm.note}
                  onChange={(e) =>
                    setManualForm((f) => ({ ...f, note: e.target.value }))
                  }
                  maxLength={255}
                />
              </div>
            </div>

            {/* Delivery */}
            <p className={styles.manualSection}>التوصيل والدفع</p>
            <div className={styles.manualGrid}>
              <div className={styles.manualField}>
                <label className={styles.manualLabel}>المنطقة *</label>
                <select
                  className={styles.manualInput}
                  value={manualForm.delivery_area}
                  onChange={(e) => handleManualZoneChange(e.target.value)}
                  required
                >
                  <option value="">اختر منطقة</option>
                  {deliveryZones.map((z) => (
                    <option key={z.id} value={z.area}>
                      {z.area} — صغيرة: {z.smallVanFees}₪ | كبيرة:{" "}
                      {z.bigVanFees}₪
                    </option>
                  ))}
                  <option value="__custom">أخرى</option>
                </select>
              </div>
              {(manualForm.delivery_area === "__custom" ||
                !deliveryZones.find(
                  (z) => z.area === manualForm.delivery_area,
                )) && (
                <div className={styles.manualField}>
                  <label className={styles.manualLabel}>اسم المنطقة</label>
                  <input
                    className={styles.manualInput}
                    value={
                      manualForm.delivery_area === "__custom"
                        ? ""
                        : manualForm.delivery_area
                    }
                    onChange={(e) =>
                      setManualForm((f) => ({
                        ...f,
                        delivery_area: e.target.value,
                      }))
                    }
                    placeholder="مثال: رام الله"
                    maxLength={30}
                  />
                </div>
              )}
              <div className={styles.manualField}>
                <label className={styles.manualLabel}>رسوم التوصيل (₪)</label>
                <input
                  className={styles.manualInput}
                  type="number"
                  min="0"
                  value={manualForm.delivery_fees}
                  onChange={(e) =>
                    setManualForm((f) => ({
                      ...f,
                      delivery_fees: e.target.value,
                    }))
                  }
                />
              </div>
              <div className={styles.manualField}>
                <label className={styles.manualLabel}>طريقة الدفع</label>
                <select
                  className={styles.manualInput}
                  value={manualForm.payment}
                  onChange={(e) =>
                    setManualForm((f) => ({ ...f, payment: e.target.value }))
                  }
                >
                  <option value="cash on delivery">نقداً عند الاستلام</option>
                  <option value="credit card">بطاقة ائتمان</option>
                </select>
              </div>
              <div className={styles.manualField}>
                <label className={styles.manualLabel}>دفعة مقدمة / عربون (₪)</label>
                <input
                  className={styles.manualInput}
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0"
                  value={manualForm.deposit}
                  onChange={(e) =>
                    setManualForm((f) => ({ ...f, deposit: e.target.value }))
                  }
                />
              </div>
              <div className={styles.manualField}>
                <label className={styles.manualLabel}>
                  قناة البيع (اختياري)
                </label>
                <select
                  className={styles.manualInput}
                  value={manualForm.sales_channel_id}
                  onChange={(e) =>
                    setManualForm((f) => ({
                      ...f,
                      sales_channel_id: e.target.value,
                    }))
                  }
                >
                  <option value="">بدون قناة</option>
                  {channels.map((ch) => (
                    <option key={ch.id} value={ch.id}>
                      {ch.icon ? `${ch.icon} ` : ""}
                      {ch.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Items */}
            <p className={styles.manualSection}>المنتجات</p>
            {manualItems.map((item, i) => (
              <div key={i} className={styles.manualItemRow}>
                <div className={styles.manualItemGrid}>
                  {/* Product picker */}
                  <div
                    className={styles.manualField}
                    style={{ gridColumn: "span 2" }}
                  >
                    <label className={styles.manualLabel}>
                      اختر من المنتجات (اختياري)
                    </label>
                    <Select
                      options={catalogProducts.map((p) => ({
                        value: p.product_id,
                        label: `${p.name}${p.code ? ` — ${p.code}` : ""}`,
                        product: p,
                      }))}
                      value={
                        item.productId
                          ? {
                              value: item.productId,
                              label:
                                catalogProducts.find(
                                  (p) => p.product_id === item.productId,
                                )?.name ?? "",
                            }
                          : null
                      }
                      onChange={(opt) => handleItemProductChange(i, opt)}
                      isClearable
                      placeholder="ابحث عن منتج..."
                      noOptionsMessage={() => "لا توجد نتائج"}
                      classNamePrefix="msel"
                      styles={{
                        control: (b) => ({
                          ...b,
                          minHeight: "2.4rem",
                          fontSize: "0.85rem",
                        }),
                        menu: (b) => ({ ...b, zIndex: 50 }),
                      }}
                    />
                  </div>
                  {/* Name — pre-filled from product, always editable */}
                  <div
                    className={styles.manualField}
                    style={{ gridColumn: "span 2" }}
                  >
                    <label className={styles.manualLabel}>اسم المنتج *</label>
                    <input
                      className={styles.manualInput}
                      value={item.name}
                      required
                      onChange={(e) =>
                        updateManualItem(i, "name", e.target.value)
                      }
                      maxLength={100}
                    />
                  </div>
                  {/* Dimension picker */}
                  <div
                    className={styles.manualField}
                    style={{ gridColumn: "span 2" }}
                  >
                    <label className={styles.manualLabel}>
                      اختر مقاساً (اختياري)
                    </label>
                    <select
                      className={styles.manualInput}
                      value={item.dimensionId ?? ""}
                      onChange={(e) =>
                        handleItemDimensionChange(
                          i,
                          e.target.value ? Number(e.target.value) : null,
                        )
                      }
                    >
                      <option value="">— أدخل الأبعاد يدوياً —</option>
                      {catalogDimensions.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.width} × {d.height} سم &nbsp;|&nbsp; مط:{" "}
                          {d.mattePrice}₪ &nbsp; لامع: {d.glossPrice}₪ &nbsp;(
                          {d.vanType === "big" ? "سيارة كبيرة" : "سيارة صغيرة"})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.manualField}>
                    <label className={styles.manualLabel}>العرض (سم) *</label>
                    <input
                      className={styles.manualInput}
                      type="number"
                      min="1"
                      value={item.width}
                      required
                      onChange={(e) =>
                        updateManualItem(i, "width", e.target.value)
                      }
                    />
                  </div>
                  <div className={styles.manualField}>
                    <label className={styles.manualLabel}>
                      الارتفاع (سم) *
                    </label>
                    <input
                      className={styles.manualInput}
                      type="number"
                      min="1"
                      value={item.height}
                      required
                      onChange={(e) =>
                        updateManualItem(i, "height", e.target.value)
                      }
                    />
                  </div>
                  <div className={styles.manualField}>
                    <label className={styles.manualLabel}>المادة</label>
                    <select
                      className={styles.manualInput}
                      value={item.material}
                      onChange={(e) =>
                        handleItemMaterialChange(i, e.target.value)
                      }
                    >
                      <option value="matte">مط</option>
                      <option value="gloss">لامع</option>
                    </select>
                  </div>
                  <div className={styles.manualField}>
                    <label className={styles.manualLabel}>السعر (₪) *</label>
                    <input
                      className={styles.manualInput}
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.price}
                      required
                      onChange={(e) =>
                        updateManualItem(i, "price", e.target.value)
                      }
                    />
                  </div>
                  <div className={styles.manualField}>
                    <label className={styles.manualLabel}>الكمية *</label>
                    <input
                      className={styles.manualInput}
                      type="number"
                      min="1"
                      value={item.quantity}
                      required
                      onChange={(e) =>
                        updateManualItem(i, "quantity", e.target.value)
                      }
                    />
                  </div>
                </div>
                {manualItems.length > 1 && (
                  <button
                    type="button"
                    className={styles.removeItemBtn}
                    onClick={() => removeManualItem(i)}
                  >
                    <MdDeleteOutline />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className={styles.addItemBtn}
              onClick={addManualItem}
            >
              <MdAdd /> إضافة منتج
            </button>

            <div className={styles.manualFormActions}>
              <button
                type="submit"
                className={styles.manualSubmitBtn}
                disabled={manualSaving}
              >
                {manualSaving ? (
                  <ImSpinner2 className="smallspinner" />
                ) : (
                  "إنشاء الطلب"
                )}
              </button>
              <button
                type="button"
                className={styles.manualCancelBtn}
                onClick={() => setShowManualForm(false)}
              >
                إلغاء
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Professional Filter Panel ── */}
      <div className={styles.filterPanel}>
        <button
          className={styles.filterPanelHead}
          onClick={() => setFiltersOpen((v) => !v)}
        >
          <span className={styles.filterPanelTitle}>
            <MdFilterList className={styles.filterPanelIcon} />
            فلاتر البحث
            {activeFilterCount > 0 && (
              <span className={styles.filterCountBadge}>
                {activeFilterCount}
              </span>
            )}
          </span>
          <span className={styles.filterPanelRight}>
            {activeFilterCount > 0 && (
              <span
                className={styles.clearAllBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  clearAllFilters();
                }}
              >
                مسح الكل
              </span>
            )}
            <MdKeyboardArrowDown
              className={`${styles.filterChevron}${filtersOpen ? ` ${styles.filterChevronOpen}` : ""}`}
            />
          </span>
        </button>

        {filtersOpen && (
          <div className={styles.filterBody}>
            {/* Status */}
            <div className={styles.filterGroup}>
              <span className={styles.filterGroupLabel}>الحالة</span>
              <div className={styles.filterPills}>
                {[
                  { value: "new", label: "جديد", cls: styles.pillNew },
                  { value: "preparing", label: "تحضير", cls: styles.pillPrep },
                  { value: "printing", label: "طباعة", cls: styles.pillPrint },
                  { value: "pressing", label: "تكبيس", cls: styles.pillPress },
                  { value: "wrapping", label: "تغليف", cls: styles.pillWrap },
                  {
                    value: "out for delivery",
                    label: "خرج للتوصيل",
                    cls: styles.pillOut,
                  },
                  {
                    value: "delivered",
                    label: "تم التوصيل",
                    cls: styles.pillDone,
                  },
                  {
                    value: "cancelled",
                    label: "تم الالغاء",
                    cls: styles.pillCancel,
                  },
                ].map((pill) => (
                  <button
                    key={pill.value}
                    className={`${styles.filterPill} ${pill.cls}${statusFilter === pill.value ? ` ${styles.pillActive}` : ""}`}
                    onClick={() => {
                      setStatusFilter((f) =>
                        f === pill.value ? null : pill.value,
                      );
                      setCurrentPage(1);
                    }}
                  >
                    <span className={styles.pillDot} />
                    {pill.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Source */}
            <div className={styles.filterGroup}>
              <span className={styles.filterGroupLabel}>المصدر</span>
              <div className={styles.filterPills}>
                {[
                  { value: "website", label: "الموقع 🌐" },
                  { value: "manual", label: "يدوي ✏️" },
                ].map((s) => (
                  <button
                    key={s.value}
                    className={`${styles.filterPill} ${styles.pillSource}${sourceFilter === s.value ? ` ${styles.pillActive} ${styles.pillSourceActive}` : ""}`}
                    onClick={() => {
                      setSourceFilter((f) => (f === s.value ? null : s.value));
                      setCurrentPage(1);
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Sales Channels */}
            {channels.length > 0 && (
              <div className={styles.filterGroup}>
                <span className={styles.filterGroupLabel}>قناة البيع</span>
                <div className={styles.filterPills}>
                  {channels.map((ch) => (
                    <button
                      key={ch.id}
                      className={`${styles.filterPill} ${styles.pillChannel}${channelFilter === ch.id ? ` ${styles.pillActive} ${styles.pillChannelActive}` : ""}`}
                      style={
                        channelFilter === ch.id
                          ? {
                              background: `${ch.color}22`,
                              borderColor: ch.color,
                              color: ch.color,
                            }
                          : { color: ch.color ?? "var(--ink-soft)" }
                      }
                      onClick={() => {
                        setChannelFilter((f) => (f === ch.id ? null : ch.id));
                        setCurrentPage(1);
                      }}
                    >
                      {ch.icon && <span>{ch.icon}</span>}
                      {ch.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Material */}
            <div className={styles.filterGroup}>
              <span className={styles.filterGroupLabel}>المادة</span>
              <div className={styles.filterPills}>
                {[
                  { value: "matte", label: "مط", cls: styles.pillMatte },
                  { value: "gloss", label: "لامع", cls: styles.pillGloss },
                ].map((pill) => (
                  <button
                    key={pill.value}
                    className={`${styles.filterPill} ${pill.cls}${materialFilter === pill.value ? ` ${styles.pillActive}` : ""}`}
                    onClick={() => {
                      setMaterialFilter((f) =>
                        f === pill.value ? null : pill.value,
                      );
                      setCurrentPage(1);
                    }}
                  >
                    {pill.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Date range */}
            <div className={styles.filterGroup}>
              <span className={styles.filterGroupLabel}>التاريخ</span>
              <div className={styles.dateRange}>
                <input
                  type="date"
                  className={styles.dateInput}
                  value={dateFrom}
                  max={dateTo || undefined}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                <span className={styles.dateSep}>—</span>
                <input
                  type="date"
                  className={styles.dateInput}
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                {(dateFrom || dateTo) && (
                  <button
                    className={styles.dateClearBtn}
                    onClick={() => {
                      setDateFrom("");
                      setDateTo("");
                      setCurrentPage(1);
                    }}
                    title="مسح التاريخ"
                  >
                    <MdClose />
                  </button>
                )}
              </div>
            </div>

            {/* Other */}
            <div className={styles.filterGroup}>
              <span className={styles.filterGroupLabel}>أخرى</span>
              <div className={styles.filterPills}>
                <button
                  className={`${styles.filterPill} ${styles.pillPhotos}${photosFilter === "yes" ? ` ${styles.pillActive}` : ""}`}
                  onClick={() => {
                    setPhotosFilter((v) => (v === "yes" ? null : "yes"));
                    setCurrentPage(1);
                  }}
                >
                  <FaCamera style={{ fontSize: "0.75rem" }} /> صور حية
                </button>
                <button
                  className={`${styles.filterPill} ${styles.pillPhotos}${photosFilter === "no" ? ` ${styles.pillActive}` : ""}`}
                  onClick={() => {
                    setPhotosFilter((v) => (v === "no" ? null : "no"));
                    setCurrentPage(1);
                  }}
                >
                  <FaCamera style={{ fontSize: "0.75rem" }} /> بدون صور حية
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Content ── */}
      {isLoadingInitial ? (
        <PanelSkeleton count={2} />
      ) : (
        <>
          {orders.length > 0 && (
            <CuttingSheet
              orders={orders}
              statusFilter={statusFilter}
              hasMore={totalPages > 1}
            />
          )}

          {orders.length === 0 ? (
            <p className="warn">لم نجد أى طلبات تتوافق مع معايير البحث</p>
          ) : (
            <div className="panelcont" style={{ margin: "0 auto" }}>
              {orders.map((o, i) => {
                const next = nextStatus(o.status);
                let total = 0;
                for (const item of o.items)
                  total += (item.price || 0) * (item.quantity || 1);
                const discount = ((o.discount * total) / 100).toFixed(2);
                const net = (
                  total -
                  Number(discount || 0) +
                  Number(o.fees || 0)
                ).toFixed(2);
                const dateStr = new Date(o.createdAt).toLocaleDateString("ar", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                });

                return (
                  <div
                    key={o.id}
                    className={`${styles.card} ${STATUS_CARD[o.status] || ""}`}
                    ref={(el) => (panelRefs.current[o.id] = el)}
                    style={{ animationDelay: `${i * 0.05}s` }}
                  >
                    {/* ── Card header ── */}
                    <div className={`upperPanel ${styles.cardHead}`}>
                      <p className={styles.orderId}>
                        <span className={styles.orderIdLabel}>طلب #</span>
                        {o.id}
                        {o.photos?.length > 0 && (
                          <span className={styles.liveBadge}>
                            <FaCamera />
                            {o.photos.length}
                          </span>
                        )}
                        {o.isManual && (
                          <span className={styles.manualBadge}>يدوي</span>
                        )}
                      </p>

                      <span
                        className={`${styles.statusBadge} ${STATUS_BADGE[o.status] || ""}`}
                      >
                        <span className={styles.dot} />
                        {mapToArabic(o.status).text}
                      </span>

                      <div className={styles.payIcon}>
                        {o.payment === "cash on delivery" ? (
                          <MdAttachMoney />
                        ) : (
                          <HiCreditCard className={styles.payIconVisa} />
                        )}
                      </div>

                      <p className={styles.customerName}>{o.name}</p>
                      <span className={styles.orderDate}>{dateStr}</span>

                      {/* Channel badge */}
                      {o.salesChannel && (
                        <span
                          className={styles.channelBadge}
                          style={{
                            background: o.salesChannel.color
                              ? `${o.salesChannel.color}18`
                              : "var(--surface-2)",
                            color: o.salesChannel.color ?? "var(--ink-soft)",
                            borderColor: o.salesChannel.color
                              ? `${o.salesChannel.color}44`
                              : "var(--line-strong)",
                          }}
                        >
                          {o.salesChannel.icon && (
                            <span>{o.salesChannel.icon}</span>
                          )}
                          <FiShare2 style={{ fontSize: "0.72rem" }} />
                          {o.salesChannel.name}
                        </span>
                      )}

                      <div className={styles.netRow}>
                        <span>الصافى</span>
                        <span className={styles.netValue}>{net} ₪</span>
                      </div>
                      {o.deposit > 0 && (
                        <div className={styles.depositBadge}>
                          <span>عربون {Number(o.deposit).toFixed(2)} ₪</span>
                          <span className={styles.remainingBadge}>
                            متبقي {(Number(net) - Number(o.deposit)).toFixed(2)} ₪
                          </span>
                        </div>
                      )}

                      <button
                        className={styles.expandBtn}
                        onClick={() => toggleExpand(o.id)}
                      >
                        {expanded[o.id] ? "إخفاء" : "التفاصيل"}
                        <MdKeyboardArrowDown
                          className={`${styles.expandIcon}${expanded[o.id] ? ` ${styles.expandIconOpen}` : ""}`}
                        />
                      </button>
                    </div>

                    {/* ── Expanded body ── */}
                    {expanded[o.id] && (
                      <div className={styles.expandBody}>
                        {/* Contact + address */}
                        <div>
                          <p className={styles.sectionLabel}>بيانات التواصل</p>
                          <div className={styles.infoBlock}>
                            {[
                              { label: "الاسم", val: o.name },
                              {
                                label: "الهاتف",
                                val: o.mobile,
                                ltr: true,
                                whatsapp: true,
                              },
                              { label: "البريد", val: o.email, ltr: true },
                              {
                                label: "العنوان",
                                val: `${o.street}${o.district ? `، ${o.district}` : ""}`,
                              },
                              { label: "منطقة التوصيل", val: o.area },
                            ].map(({ label, val, ltr, whatsapp }) => (
                              <div key={label} className={styles.infoRow}>
                                <span className={styles.infoLabel}>
                                  {label}
                                </span>
                                <p
                                  className={`${styles.infoValue}${ltr ? ` ${styles.infoValueLtr}` : ""}`}
                                >
                                  {val}
                                </p>
                                {whatsapp && val && (
                                  <a
                                    href={`https://wa.me/${val.replace(/\D/g, "")}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.whatsappBtn}
                                    title="ارسال رسالة واتساب"
                                  >
                                    <FaWhatsapp />
                                  </a>
                                )}
                              </div>
                            ))}
                            {o.salesChannel && (
                              <div className={styles.infoRow}>
                                <span className={styles.infoLabel}>
                                  قناة البيع
                                </span>
                                <p className={styles.infoValue}>
                                  {o.salesChannel.icon} {o.salesChannel.name}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        {o.note && (
                          <div className={styles.noteBlock}>
                            <span className={styles.noteLabel}>ملاحظة</span>
                            {o.note}
                          </div>
                        )}

                        {/* Items */}
                        <div>
                          <p className={styles.sectionLabel}>المنتجات</p>
                          <div className={styles.itemsList}>
                            {o.items.map((item, idx) => (
                              <div
                                key={item.id}
                                className={styles.item}
                                style={{ animationDelay: `${idx * 0.05}s` }}
                                onClick={() =>
                                  item.product_id &&
                                  navigate(`/product/${item.product_id}`)
                                }
                              >
                                <div className={styles.itemThumb}>
                                  <ProductImage
                                    url={item.image_url}
                                    alt={item.name}
                                    loading="lazy"
                                  />
                                </div>
                                <div className={styles.itemInfo}>
                                  <p className={styles.itemName}>{item.name}</p>
                                  {item.code && (
                                    <p className={styles.itemCode}>
                                      كود {item.code}
                                    </p>
                                  )}
                                  <div className={styles.itemTags}>
                                    <span className={styles.itemTag}>
                                      {item.width} × {item.height}
                                    </span>
                                    <span className={styles.itemTag}>
                                      {mapToArabic(item.material).text}
                                    </span>
                                  </div>
                                </div>
                                <div className={styles.itemPricing}>
                                  <span className={styles.itemQty}>
                                    {item.quantity} × {item.price} ₪
                                  </span>
                                  <span className={styles.itemTotal}>
                                    {(item.price * item.quantity).toFixed(2)} ₪
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Totals */}
                        <div>
                          <p className={styles.sectionLabel}>الحساب</p>
                          <div className={styles.totalsBlock}>
                            <div className={styles.totalRow}>
                              <span className={styles.totalLabel}>
                                الإجمالى
                              </span>
                              <span className={styles.totalVal}>
                                {total.toFixed(2)} ₪
                              </span>
                            </div>
                            <div className={styles.totalRow}>
                              <span className={styles.totalLabel}>
                                رسوم التوصيل
                              </span>
                              <span className={styles.totalVal}>
                                {o.fees || 0} ₪
                              </span>
                            </div>
                            {o.promocode && (
                              <div className={styles.totalRow}>
                                <span className={styles.totalLabel}>
                                  خصم ({o.discount}٪)
                                </span>
                                <span
                                  className={`${styles.totalVal} ${styles.discountVal}`}
                                >
                                  - {discount} ₪
                                </span>
                              </div>
                            )}
                            <div className={styles.netDivider} />
                            <div
                              className={`${styles.totalRow} ${styles.totalNetRow}`}
                            >
                              <span className={styles.totalNetLabel}>
                                الصافى
                              </span>
                              <span className={styles.totalNetVal}>
                                {net} ₪
                              </span>
                            </div>
                            {o.deposit > 0 && (
                              <>
                                <div className={styles.netDivider} />
                                <div className={styles.totalRow}>
                                  <span className={styles.totalLabel}>العربون المدفوع</span>
                                  <span className={`${styles.totalVal} ${styles.depositPaidVal}`}>
                                    {Number(o.deposit).toFixed(2)} ₪
                                  </span>
                                </div>
                                <div className={`${styles.totalRow} ${styles.totalNetRow}`}>
                                  <span className={styles.totalNetLabel}>المبلغ المتبقي</span>
                                  <span className={`${styles.totalNetVal} ${styles.remainingVal}`}>
                                    {(Number(net) - Number(o.deposit)).toFixed(2)} ₪
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Live Photos */}
                        <div>
                          <p className={styles.sectionLabel}>الصور الحية</p>
                          <div className={styles.photoGrid}>
                            {o.photos?.map((p, pi) => (
                              <div
                                key={p.id}
                                className={styles.photoThumb}
                                style={{ animationDelay: `${pi * 0.04}s` }}
                              >
                                <ProductImage
                                  url={p.url}
                                  alt="صورة حية"
                                  loading="lazy"
                                />
                                <button
                                  className={styles.photoDeleteBtn}
                                  onClick={() =>
                                    !deletingPhoto &&
                                    handleDeletePhoto(o.id, p.id)
                                  }
                                  disabled={deletingPhoto}
                                >
                                  <FaRegTrashAlt />
                                </button>
                              </div>
                            ))}
                            <label
                              className={`${styles.photoAddBtn}${uploadingPhotos[o.id] ? ` ${styles.photoAddBtnLoading}` : ""}`}
                            >
                              {uploadingPhotos[o.id] ? (
                                <ImSpinner2 className="smallspinner" />
                              ) : (
                                <>
                                  <MdAddPhotoAlternate
                                    className={styles.photoAddIcon}
                                  />
                                  <span>إضافة</span>
                                </>
                              )}
                              <input
                                type="file"
                                multiple
                                accept="image/*"
                                hidden
                                ref={(el) => (photoInputRef.current[o.id] = el)}
                                onChange={(e) =>
                                  handleAddPhotos(o.id, e.target.files)
                                }
                              />
                            </label>
                          </div>
                        </div>

                        {/* Actions */}
                        {o.status !== "delivered" &&
                          o.status !== "cancelled" && (
                            <div className={styles.actions}>
                              <button
                                className={styles.nextBtn}
                                disabled={changing || cancelling}
                                onClick={() =>
                                  handleStatus(
                                    o.id,
                                    o.userId,
                                    o.name,
                                    o.email,
                                    next,
                                  )
                                }
                              >
                                {changing ? (
                                  <ImSpinner2 className="smallspinner" />
                                ) : (
                                  <>
                                    <MdArrowForward /> {mapToArabic(next).text}
                                  </>
                                )}
                              </button>
                              <button
                                className={`${styles.cancelBtn}${changing || cancelling ? ` ${styles.cancelBtnOff}` : ""}`}
                                onClick={() =>
                                  changing || cancelling
                                    ? null
                                    : handleCancel(o.id, o.userId, o.email)
                                }
                              >
                                <FaRegTrashAlt />
                              </button>
                            </div>
                          )}

                        {/* Print */}
                        <div className={styles.printBtnGroup}>
                          <button
                            className={styles.printBtn}
                            onClick={() =>
                              printOrder(o, total, discount, net, dateStr, true, o.deposit)
                            }
                          >
                            <MdPrint /> مع السعر
                          </button>
                          <button
                            className={styles.printBtn}
                            onClick={() =>
                              printOrder(o, total, discount, net, dateStr, false, 0)
                            }
                          >
                            <MdPrint /> بدون سعر
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  disabled={loading}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OrdersPanel;
